import { collection, doc, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { info, warn } from '../utils/logger';

export type QuoteWatchlistItemInput = {
    symbol: string;
    name?: string;
    exchange?: string;
    currency?: string;
    assetType?: 'equity' | 'etf' | 'crypto' | 'fx' | 'index';
    region?: string;
    provider?: string;
    pinned?: boolean;
    notes?: string;
    instrumentId?: string;
    providerKey?: string;
};

const normalizeExchange = (exchange?: string) => {
    const ex = String(exchange || '').trim().toUpperCase();
    if (!ex) return undefined;
    if (ex.includes('LSE') || ex.includes('XLON') || ex.includes('LON')) return 'LSE';
    if (ex.includes('NASDAQ')) return 'NASDAQ';
    if (ex.includes('NYSE')) return 'NYSE';
    return ex;
};

const toSymbolDocId = (symbol: string) => symbol.replaceAll('/', '__');


const removeUndefinedFields = <T extends Record<string, unknown>>(payload: T): T => (
    Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== undefined)) as T
);

const normalizeWatchlistItems = (items: Array<string | QuoteWatchlistItemInput>) => {
    const normalized = new Map<string, QuoteWatchlistItemInput>();
    items.forEach((item) => {
        const rawSymbol = typeof item === 'string' ? item : item.symbol;
        const symbol = rawSymbol?.trim().toUpperCase();
        if (!symbol) return;
        const base = normalized.get(symbol) || { symbol };
        if (typeof item === 'string') {
            normalized.set(symbol, base);
            return;
        }
        normalized.set(symbol, { ...base, ...item, symbol });
    });

    return Array.from(normalized.values());
};

export const upsertQuoteWatchlistSymbols = async (
    userId: string,
    symbols: Array<string | QuoteWatchlistItemInput>
): Promise<void> => {
    if (!userId) {
        warn('[quoteWatchlist] upsert skipped (no user id)');
        return;
    }
    const normalized = normalizeWatchlistItems(symbols);
    if (!normalized.length) {
        info('[quoteWatchlist] upsert skipped (no symbols)');
        return;
    }

    const watchlistCollection = collection(db, 'users', userId, 'watchlist');

    info('[quoteWatchlist] upserting symbols', {
        count: normalized.length,
        userId,
        sample: normalized.slice(0, 5).map((item) => item.symbol),
    });

    await Promise.all(
        normalized.map(async (item) => {
            const symbolDocId = toSymbolDocId(item.symbol);
            const watchlistRef = doc(watchlistCollection, symbolDocId);
            const trackedRef = doc(db, 'tracked_symbols', symbolDocId);
            await runTransaction(db, async (transaction) => {
                const watchlistSnapshot = await transaction.get(watchlistRef);
                const trackedSnapshot = await transaction.get(trackedRef);
                const isNewWatchlistEntry = !watchlistSnapshot.exists();
                const trackedData = trackedSnapshot.data() || {};
                const currentCount = trackedData.activeUserCount ?? 0;
                const trackedExchange = trackedData.exchange;
                const trackedCurrency = trackedData.currency;
                const trackedName = trackedData.name;
                const trackedInstrumentId = trackedData.instrumentId;
                const trackedProviderKey = trackedData.providerKey;
                const watchlistData = watchlistSnapshot.data() || {};

                const payload: QuoteWatchlistItemInput & {
                    symbol: string;
                    lastSeenAt: ReturnType<typeof serverTimestamp>;
                    addedAt?: ReturnType<typeof serverTimestamp>;
                    exchange: string | null;
                    currency: string | null;
                    name: string | null;
                    instrumentId: string | null;
                    providerKey: string | null;
                } = {
                    ...item,
                    symbol: item.symbol,
                    exchange: normalizeExchange(item.exchange) ?? trackedExchange ?? watchlistData.exchange ?? null,
                    currency: item.currency ?? trackedCurrency ?? watchlistData.currency ?? null,
                    name: item.name ?? trackedName ?? watchlistData.name ?? null,
                    instrumentId: item.instrumentId ?? trackedInstrumentId ?? watchlistData.instrumentId ?? null,
                    providerKey: item.providerKey ?? trackedProviderKey ?? watchlistData.providerKey ?? null,
                    lastSeenAt: serverTimestamp(),
                };

                if (isNewWatchlistEntry || !watchlistData.addedAt) {
                    payload.addedAt = serverTimestamp();
                }

                transaction.set(watchlistRef, removeUndefinedFields(payload), { merge: true });

                transaction.set(
                    trackedRef,
                    removeUndefinedFields({
                        symbol: item.symbol,
                        exchange: normalizeExchange(item.exchange) ?? trackedData.exchange ?? null,
                        currency: item.currency ?? trackedData.currency ?? null,
                        name: item.name ?? trackedData.name ?? null,
                        instrumentId: item.instrumentId ?? trackedData.instrumentId ?? null,
                        providerKey: item.providerKey ?? trackedData.providerKey ?? null,
                        activeUserCount: isNewWatchlistEntry ? currentCount + 1 : Math.max(currentCount, 1),
                        status: 'active',
                        lastAddedAt: serverTimestamp(),
                    }),
                    { merge: true }
                );
            });
        })
    );

    info('[quoteWatchlist] upsert committed', { count: normalized.length, userId });
};

export const removeQuoteWatchlistSymbols = async (userId: string, symbols: string[]): Promise<void> => {
    if (!userId) {
        warn('[quoteWatchlist] remove skipped (no user id)');
        return;
    }
    const normalized = normalizeWatchlistItems(symbols).map((item) => item.symbol);
    if (!normalized.length) {
        info('[quoteWatchlist] remove skipped (no symbols)');
        return;
    }

    const watchlistCollection = collection(db, 'users', userId, 'watchlist');

    info('[quoteWatchlist] removing symbols', {
        count: normalized.length,
        userId,
        sample: normalized.slice(0, 5),
    });

    await Promise.all(
        normalized.map(async (symbol) => {
            const symbolDocId = toSymbolDocId(symbol);
            const watchlistRef = doc(watchlistCollection, symbolDocId);
            const trackedRef = doc(db, 'tracked_symbols', symbolDocId);
            await runTransaction(db, async (transaction) => {
                const watchlistSnapshot = await transaction.get(watchlistRef);
                if (!watchlistSnapshot.exists()) {
                    return;
                }

                const trackedSnapshot = await transaction.get(trackedRef);
                transaction.delete(watchlistRef);

                if (!trackedSnapshot.exists()) {
                    return;
                }

                const currentCount = trackedSnapshot.data()?.activeUserCount ?? 0;
                const nextCount = Math.max(currentCount - 1, 0);
                const update: {
                    activeUserCount: number;
                    status?: 'inactive';
                    lastRemovedAt?: ReturnType<typeof serverTimestamp>;
                } = { activeUserCount: nextCount };

                if (nextCount === 0) {
                    update.status = 'inactive';
                    update.lastRemovedAt = serverTimestamp();
                }

                transaction.set(trackedRef, update, { merge: true });
            });
        })
    );

    info('[quoteWatchlist] remove committed', { count: normalized.length, userId });
};
